import { useState } from 'react';
import { useCurrency } from '../../../../customHooks/useCurrency';
import { RadarChart } from './RadarChart';
import { TableShell } from './TableShell';
import {
  TABLE_BODY_TEXT,
  TABLE_CELL_BORDER,
  TABLE_HEADER_BG,
  TABLE_HEADER_TEXT_SOFT,
  TABLE_HEADER_TEXT_STRONG,
  TABLE_TOTAL_BG,
} from './tableStyles';
import { formatMoney, formatPct, formatUnits } from './formatters';

const isEmptyDim = (v) => v == null || v === '—';
const WAREHOUSE_GROUP_PALETTE = ['#374151', '#9CA3AF'];

/**
 * Radar del REPARTO del stock restante por (género · categoría
 * [· subcategoría]). Dos series superpuestas — % uds y % valor
 * (CI) sobre el total del informe — para ver dónde se está
 * acumulando la mercancía sin vender.
 *
 * Sin tope: incluye todas las combinaciones con stock > 0. Si hay
 * <3 ejes la lectura del radar no aporta y devolvemos null. La
 * escala se autoajusta a `max(share) + 5pp` redondeado al 10%
 * siguiente.
 */
export const WarehouseRadar = ({ rows, genderFilterActive, showSub = false }) => {
  const { currency } = useCurrency();
  const fmt$ = (n) => formatMoney(n, currency);
  const pct = (n) => formatPct(n);
  // Estado de ordenación de la mini-tabla del legendFooter. `null` =
  // orden natural (el mismo lex (gender, cat, [subcat]) que usa el
  // radar). Al clicar una cabecera la tabla se re-ordena; el radar
  // se queda como está (usa el `groups` original, no `sortedRows`).
  const [sortBy, setSortBy] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  // Agregación por (gender · category · [subcategory]) con sort
  // lexicográfico — los runs del grouping quedan contiguos.
  const groups = (() => {
    const map = new Map();
    for (const r of rows || []) {
      const gender = !isEmptyDim(r.gender) ? r.gender : 'Sin género';
      const category = !isEmptyDim(r.category) ? r.category : 'Sin categoría';
      const subcategory = !isEmptyDim(r.subcategory) ? r.subcategory : 'Sin subcategoría';
      const keyParts = [];
      if (!genderFilterActive) keyParts.push(gender);
      keyParts.push(category);
      if (showSub) keyParts.push(subcategory);
      const key = keyParts.join('|');
      let g = map.get(key);
      if (!g) {
        g = { gender, category, subcategory, remainUnits: 0, remainValueCI: 0 };
        map.set(key, g);
      }
      g.remainUnits += Number(r.remainUnits) || 0;
      g.remainValueCI += Number(r.remainValueCI) || 0;
    }
    const arr = [...map.values()].filter((c) => c.remainValueCI > 0 || c.remainUnits > 0);
    arr.sort((a, b) => {
      if (!genderFilterActive) {
        const gcmp = String(a.gender).localeCompare(b.gender, 'es', { sensitivity: 'base' });
        if (gcmp !== 0) return gcmp;
      }
      const ccmp = String(a.category).localeCompare(b.category, 'es', { sensitivity: 'base' });
      if (ccmp !== 0) return ccmp;
      if (showSub) return String(a.subcategory).localeCompare(b.subcategory, 'es', { sensitivity: 'base' });
      return 0;
    });
    return arr;
  })();

  if (groups.length < 3) return null;

  const totalUnits = groups.reduce((acc, c) => acc + Math.max(0, c.remainUnits), 0);
  const totalValue = groups.reduce((acc, c) => acc + Math.max(0, c.remainValueCI), 0);
  const safeShare = (num, den) => (den > 0 ? num / den : 0);

  // Ejes: primary = dim más específica, secondary = dim de contexto
  // (categoría si showSub, género si no).
  const axes = groups.map((c) => {
    if (showSub) return { primary: c.subcategory, secondary: c.category };
    return genderFilterActive ? c.category : { primary: c.category, secondary: c.gender };
  });
  // Grouping para los radios punteados + título suave dentro del radar.
  const groupingDim = !genderFilterActive ? 'gender' : showSub ? 'category' : null;
  const axisGroups = groupingDim
    ? (() => {
        const colorByKey = new Map();
        return groups.map((c) => {
          const key = c[groupingDim];
          if (!colorByKey.has(key))
            colorByKey.set(key, WAREHOUSE_GROUP_PALETTE[colorByKey.size % WAREHOUSE_GROUP_PALETTE.length]);
          return { key, color: colorByKey.get(key) };
        });
      })()
    : null;

  const series = [
    {
      label: 'Unidades',
      color: '#1F6FB2',
      formatValue: pct,
      formatRaw: (v) => `${formatUnits(v)} uds`,
      totalValue: 1,
      totalRaw: totalUnits,
      values: groups.map((c) => safeShare(Math.max(0, c.remainUnits), totalUnits)),
      rawValues: groups.map((c) => Math.max(0, c.remainUnits)),
    },
    {
      label: 'Valor (CI)',
      color: '#D17B3F',
      formatValue: pct,
      formatRaw: fmt$,
      totalValue: 1,
      totalRaw: totalValue,
      values: groups.map((c) => safeShare(Math.max(0, c.remainValueCI), totalValue)),
      rawValues: groups.map((c) => Math.max(0, c.remainValueCI)),
    },
  ];

  const allShares = [...series[0].values, ...series[1].values];
  const maxShare = allShares.length > 0 ? Math.max(...allShares) : 0;
  const scaleMax = Math.max(0.1, Math.ceil((maxShare + 0.05) * 10) / 10);

  // Tablita compacta bajo la leyenda — `RadarChart` la renderiza
  // pegada al final del panel izquierdo via `legendFooter`. Cuatro
  // columnas ordenables: etiqueta + uds + valor + % share. Cuando
  // `showSub` está activo, bajo la subcategoría se pinta la
  // categoría en pequeñito subdued para no perder contexto.
  const labelOf = (g) => (showSub ? g.subcategory : g.category);
  // Orden por columna. `null` = natural (lex), texto por defecto ASC,
  // numérico DESC (relevante primero).
  const handleSort = (id) => {
    if (id === sortBy) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(id);
    setSortDir(id === 'label' ? 'asc' : 'desc');
  };
  const sortedRows = (() => {
    if (sortBy == null) return groups;
    const sign = sortDir === 'desc' ? -1 : 1;
    const arr = [...groups];
    arr.sort((a, b) => {
      if (sortBy === 'label') {
        return String(labelOf(a)).localeCompare(String(labelOf(b)), 'es', { sensitivity: 'base' }) * sign;
      }
      const key = sortBy === 'units' ? 'remainUnits' : 'remainValueCI'; // value y share comparten campo
      return ((Number(a[key]) || 0) - (Number(b[key]) || 0)) * sign;
    });
    return arr;
  })();
  const arrowFor = (id) => (sortBy === id ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const thStyle = {
    padding: '6px 6px',
    fontSize: 10,
    fontWeight: 600,
    color: TABLE_HEADER_TEXT_STRONG,
    backgroundColor: TABLE_HEADER_BG,
    borderBottom: TABLE_CELL_BORDER,
    whiteSpace: 'nowrap',
  };
  const tdBase = {
    padding: '6px 6px',
    fontSize: 11,
    color: TABLE_BODY_TEXT,
    borderBottom: TABLE_CELL_BORDER,
    whiteSpace: 'nowrap',
  };
  const legendFooter = (
    <div style={{ marginTop: 14 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: TABLE_HEADER_TEXT_STRONG,
          marginBottom: 6,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        Detalle
      </div>
      <TableShell>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {[
                  { id: 'label', align: 'left', label: showSub ? 'Subcat.' : 'Categoría' },
                  { id: 'units', align: 'right', label: 'Uds' },
                  { id: 'value', align: 'right', label: 'Valor' },
                  { id: 'share', align: 'right', label: '%' },
                ].map((c) => {
                  const active = sortBy === c.id;
                  return (
                    <th
                      key={c.id}
                      onClick={() => handleSort(c.id)}
                      title="Ordenar por esta columna"
                      style={{
                        ...thStyle,
                        textAlign: c.align,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontWeight: active ? 700 : 600,
                      }}
                    >
                      {c.label}
                      {arrowFor(c.id)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((g, i) => {
                const label = labelOf(g);
                const shareVal = totalValue > 0 ? g.remainValueCI / totalValue : 0;
                return (
                  <tr key={`${label}-${i}`} className="efftable-row">
                    <td style={{ ...tdBase, maxWidth: 130 }}>
                      <div
                        title={label}
                        style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {label}
                      </div>
                      {showSub && !isEmptyDim(g.category) && g.category !== label && (
                        <div
                          title={g.category}
                          style={{
                            fontSize: 10,
                            color: TABLE_HEADER_TEXT_SOFT,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            marginTop: 1,
                          }}
                        >
                          {g.category}
                        </div>
                      )}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'right' }}>{formatUnits(g.remainUnits)}</td>
                    <td style={{ ...tdBase, textAlign: 'right' }}>{fmt$(g.remainValueCI)}</td>
                    <td style={{ ...tdBase, textAlign: 'right', color: TABLE_HEADER_TEXT_SOFT }}>
                      {formatPct(shareVal)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Totales: suma de uds y valor — la suma de shares es 100%
                por construcción, así que la pintamos directamente. */}
            <tfoot>
              <tr style={{ backgroundColor: TABLE_TOTAL_BG, fontWeight: 600 }}>
                <td
                  style={{
                    ...tdBase,
                    fontWeight: 700,
                    color: TABLE_HEADER_TEXT_STRONG,
                    borderTop: `2px solid ${TABLE_CELL_BORDER.split(' ')[2]}`,
                    borderBottom: 'none',
                  }}
                >
                  Total
                </td>
                <td
                  style={{
                    ...tdBase,
                    textAlign: 'right',
                    fontWeight: 700,
                    borderTop: `2px solid ${TABLE_CELL_BORDER.split(' ')[2]}`,
                    borderBottom: 'none',
                  }}
                >
                  {formatUnits(totalUnits)}
                </td>
                <td
                  style={{
                    ...tdBase,
                    textAlign: 'right',
                    fontWeight: 700,
                    borderTop: `2px solid ${TABLE_CELL_BORDER.split(' ')[2]}`,
                    borderBottom: 'none',
                  }}
                >
                  {fmt$(totalValue)}
                </td>
                <td
                  style={{
                    ...tdBase,
                    textAlign: 'right',
                    fontWeight: 700,
                    color: TABLE_HEADER_TEXT_SOFT,
                    borderTop: `2px solid ${TABLE_CELL_BORDER.split(' ')[2]}`,
                    borderBottom: 'none',
                  }}
                >
                  100%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </TableShell>
    </div>
  );

  return (
    <RadarChart
      title="Reparto por categoría"
      helpText="% de cada categoría sobre el total del stock restante. Escala recortada al máximo + 5 puntos."
      axes={axes}
      axisGroups={axisGroups}
      series={series}
      scaleMax={scaleMax}
      legendFooter={legendFooter}
      legendPosition="right"
      legendMinWidth={290}
    />
  );
};
